import {
  observable,
  action,
  extendObservable,
  makeObservable,
  reaction,
} from 'mobx'

import DOMPurify from '@client/utils/dompurify'
import { BlockModel } from '@client/components/Block/model'
import { defaultSettings } from '@client/components/blocks/Text/constants'
import { MIN_WIDTH } from '@client/components/Block/constants'

export default class ProductModel extends BlockModel {
  constructor(data = {}, editor) {
    super(data)

    makeObservable(this)

    this.editor = editor

    this.settings.set({ ...defaultSettings, ...data.settings })

    extendObservable(this, {
      htmlText: DOMPurify.sanitize(data.htmlText) || '',
      color: data.color || null,
    })

    this._desktopContainerWidth = 0

    reaction(
      () => this.containerDimensions.width,
      (containerWidth) => {
        const prevDesktopContainerWidth = this._desktopContainerWidth

        if (this.editor?.viewMode !== 'mobile') {
          this._desktopContainerWidth = containerWidth
        }

        const values = this.settings.values
        const refWidth = this._desktopContainerWidth || containerWidth
        const mobileFullWidth = !values.fullWidth && +values.width >= refWidth

        if (this.editor?.viewMode === 'mobile') {
          this.settings.set({ mobileFullWidth })
          return
        }

        const wasPinnedToMax =
          prevDesktopContainerWidth > 0 &&
          +values.width === prevDesktopContainerWidth
        const shouldMaxWidth =
          !values.width || wasPinnedToMax || values.fullWidth
        const padding = parseFloat(values.updatedPadding) || 0
        const width = `${(containerWidth || MIN_WIDTH) - padding}`
        if (
          shouldMaxWidth &&
          containerWidth >= MIN_WIDTH &&
          width >= MIN_WIDTH &&
          +width !== +values.width
        ) {
          this.settings.set({
            containerWidth: containerWidth.toString(),
            width: `${(containerWidth || MIN_WIDTH) - padding}`,
            mobileFullWidth,
          })
        } else {
          this.settings.set({
            containerWidth: containerWidth.toString(),
            mobileFullWidth,
          })
        }
      }
    )

    reaction(
      () => this.settings.values.updatedPadding,
      (updatedPadding) => {
        const values = this.settings.values
        const padding = parseFloat(updatedPadding) || 0
        if (values.containerWidth >= MIN_WIDTH) {
          this.settings.set({
            width: `${(values.containerWidth || MIN_WIDTH) - padding}`,
            mobileFullWidth:
              !values.fullWidth && +values.width >= values.containerWidth,
          })
        } else {
          this.settings.set({
            mobileFullWidth:
              !values.fullWidth && +values.width >= values.containerWidth,
          })
        }
      }
    )
  }

  @observable containerDimensions = { width: 0, height: 0 }

  @action setContainerDimensions = (obj) => {
    if (!obj || (obj && !obj.width) || (obj && obj.width < MIN_WIDTH)) {
      return
    }
    this.containerDimensions = obj
  }

  // htmlText is sanitized at I/O (constructor/restore on input, serialize on
  // output) but is rendered as-is via dangerouslySetInnerHTML in the editor
  // preview. Callers of setHtmlText must pass already-trusted HTML (TinyMCE
  // output, or a transform of an already-trusted htmlText) — do not pipe raw
  // user input through here.
  @action setHtmlText = (val) => {
    this.htmlText = val
  }

  @action setColor = (val) => {
    this.color = val
  }

  serialize = () => {
    return {
      type: this.type,
      htmlText: DOMPurify.sanitize(this.htmlText),
      settings: this.settings.serialize(),
    }
  }

  restore = (data) => {
    this.settings.set(data.settings)
    this.htmlText = DOMPurify.sanitize(data.htmlText) || ''
  }
}

export const NULL = new ProductModel()
